/**
 * Settings -> Connected apps.
 *
 * The user-facing half of OAuth revocation. Without it the backend can revoke a connector's
 * grant but no user can ask it to — the same gap ActiveSessions was built to close for
 * browser sessions.
 *
 * ── Why this renders NOTHING when the list is empty ───────────────────────────
 * Almost every user has zero connected apps, and there is no "connect an app" action here to
 * offer them — a connection begins in the MCP client (Claude), never in Settings. An empty
 * card would be a permanent piece of furniture that no one can act on. It appears the moment
 * a grant exists, which is the moment it becomes useful.
 *
 * A failed fetch DOES render, because "we could not tell you what is connected" is worth
 * saying to someone who came here to disconnect something.
 */
import { Card } from '../common/Card';
import { useConnectedApps, useRevokeConnectedApp } from '../../hooks/useConnectedApps';
import { relativeTime as relative } from '../../utils/relativeTime';

export const ConnectedApps = () => {
  const { data, isLoading, isError } = useConnectedApps();
  const revoke = useRevokeConnectedApp();

  const apps = data ?? [];

  // See the header: nothing to manage and nothing to do means nothing to render.
  if (isLoading || (apps.length === 0 && !isError)) return null;

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-stone-900">Connected apps</h2>
        <p className="text-sm text-stone-500 mt-1">
          Applications you have given access to your OKR Hub data
        </p>
      </div>

      {isError && <p className="text-sm text-red-600">Could not load your connected apps.</p>}

      {!isError && (
        <ul className="divide-y divide-stone-200">
          {apps.map((app) => (
            <li key={app.id} className="py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-stone-900">{app.name ?? 'Unnamed app'}</p>
                <p className="text-xs text-stone-500">
                  {/* Scope is the honest statement of what the app can do, and every grant
                      this server issues is read-only. Saying so here is the difference
                      between a list of names and a list a user can make a decision from. */}
                  {app.scopes.length ? app.scopes.join(', ') : 'no scopes'} ·{' '}
                  {app.lastUsedAt
                    ? `last used ${relative(app.lastUsedAt)}`
                    : `connected ${relative(app.createdAt)}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => revoke.mutate(app.id)}
                disabled={revoke.isPending}
                className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                Disconnect
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default ConnectedApps;
